import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import ImageSkeleton from './ImageSkeleton';
import ImageWatermark from './ImageWatermark';
import { getImageCrop, getImageUrl, buildImageTransformStyle } from '../lib/imageData';

// Global debug switch for skeleton preview across the app.
// true  => force skeleton preview everywhere
// false => normal skeleton behavior (show only while media is loading)
const GLOBAL_FORCE_SKELETON = false;

export default function ImageOnlySkeletonLoader({
  image,
  src,
  alt = '',
  mode = 'cropped',
  loading = 'lazy',
  fetchpriority,
  decoding = 'async',
  videoSrc,
  videoPoster,
  videoPosterSources = [],
  imageSources = [],
  videoClassName = '',
  videoSources = [],
  containerClassName = '',
  imgClassName = '',
  style,
  aspectRatio,
  width,
  height,
  skeletonEnabled = true,
  disableSkeleton = false,
  skeletonRadius = '2xl',
  skeletonStyle,
  skeletonImgClassName = '',
  watermark = false,
  protectSaving = true,
  lazyVideo = true,
  onLoad,
  onError,
  ...imageProps
}) {
  const isSkeletonEnabled = Boolean(skeletonEnabled) && !disableSkeleton;

  const resolvedSrc = useMemo(() => {
    const fromImage = getImageUrl(image);
    if (fromImage) return fromImage;
    const fromSrc = getImageUrl(src);
    if (fromSrc) return fromSrc;
    return typeof src === 'string' ? String(src || '').trim() : '';
  }, [image, src]);

  const [isLoaded, setIsLoaded] = useState(!isSkeletonEnabled);
  const [responsiveVideoPoster, setResponsiveVideoPoster] = useState(videoPosterSources[0]?.src || videoPoster);
  const [shouldLoadVideo, setShouldLoadVideo] = useState(mode !== 'video' || !lazyVideo);
  const loadedKeyRef = useRef('');
  const cropRef = useRef(null);
  const [layout, setLayout] = useState({ width: 0, height: 0, naturalWidth: 0, naturalHeight: 0 });
  const crop = useMemo(() => (mode === 'video' ? null : getImageCrop(image)), [image, mode]);

  useEffect(() => {
    if (mode !== 'video' || !lazyVideo || typeof window === 'undefined') return undefined;

    const node = cropRef.current;
    if (!node) return undefined;

    if (typeof IntersectionObserver === 'undefined') {
      setShouldLoadVideo(true);
      return undefined;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry?.isIntersecting) return;
        setShouldLoadVideo(true);
        observer.disconnect();
      },
      { rootMargin: '300px' },
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [lazyVideo, mode]);

  useEffect(() => {
    if (mode !== 'video' || !videoPosterSources.length || typeof window === 'undefined') return undefined;

    const updatePoster = () => {
      const matchingSource = [...videoPosterSources]
        .sort((first, second) => (first.minWidth || 0) - (second.minWidth || 0))
        .filter((source) => window.innerWidth >= (source.minWidth || 0))
        .pop();
      setResponsiveVideoPoster(matchingSource?.src || videoPoster);
    };

    updatePoster();
    window.addEventListener('resize', updatePoster);
    return () => window.removeEventListener('resize', updatePoster);
  }, [mode, videoPoster, videoPosterSources]);

  useEffect(() => {
    const key = String(resolvedSrc || '').trim();

    if (!isSkeletonEnabled) {
      setIsLoaded(true);
      return;
    }

    if (!key) {
      setIsLoaded(false);
      loadedKeyRef.current = '';
      return;
    }

    if (loadedKeyRef.current !== key) {
      loadedKeyRef.current = '';
      setIsLoaded(false);
    }

    // If the browser already has the image fully cached, the underlying
    // <img>'s load event may have fired before React attached the onLoad
    // handler, so setIsLoaded would never be called. Probe the cache
    // synchronously and mark the image as loaded when it is already complete.
    // This ensures a refresh never leaves the skeleton stuck on top of an
    // already-cached image, regardless of the rendering mode.
    if (typeof window !== 'undefined') {
      const probe = new window.Image();
      probe.decoding = 'async';
      probe.src = key;
      // For cached images the browser resolves the resource asynchronously,
      // so probe.complete is still false on the very next statement. Defer the
      // check to the next microtask so we observe the post-load state.
      const finalizeProbe = () => {
        if (probe.complete && probe.naturalWidth > 0) {
          loadedKeyRef.current = key;
          setIsLoaded(true);
        }
      };
      if (probe.complete && probe.naturalWidth > 0) {
        finalizeProbe();
      } else {
        probe.addEventListener('load', finalizeProbe, { once: true });
        probe.addEventListener('error', finalizeProbe, { once: true });
      }
    }
  }, [resolvedSrc, isSkeletonEnabled, mode]);

  function handleLoad(event) {
    const key = String(resolvedSrc || '').trim();
    loadedKeyRef.current = key;
    setIsLoaded(true);
    if (crop?.rect && event?.currentTarget) {
      const naturalWidth = event.currentTarget.naturalWidth || 0;
      const naturalHeight = event.currentTarget.naturalHeight || 0;
      setLayout((prev) =>
        prev.naturalWidth === naturalWidth && prev.naturalHeight === naturalHeight
          ? prev
          : { ...prev, naturalWidth, naturalHeight }
      );
      measureContainer();
    }
    if (typeof onLoad === 'function') onLoad(event);
  }

  function handleError(event) {
    if (typeof onError === 'function') onError(event);
  }

  function handleVideoReady(event) {
    const key = String(videoSrc || resolvedSrc || '').trim();
    if (key) {
      loadedKeyRef.current = key;
    }
    setIsLoaded(true);
    if (typeof onLoad === 'function') onLoad(event);
  }

  const measureContainer = useCallback(() => {
    const node = cropRef.current;
    if (!node) return;
    const rect = node.getBoundingClientRect();
    const width = rect.width || node.clientWidth || 0;
    const height = rect.height || node.clientHeight || 0;
    setLayout((prev) =>
      prev.width === width && prev.height === height ? prev : { ...prev, width, height }
    );
  }, []);

  useEffect(() => {
    if (!crop?.rect) return undefined;
    measureContainer();
    if (typeof ResizeObserver !== 'undefined') {
      const observer = new ResizeObserver(() => {
        measureContainer();
      });
      if (cropRef.current) observer.observe(cropRef.current);
      return () => observer.disconnect();
    }
    window.addEventListener('resize', measureContainer);
    return () => window.removeEventListener('resize', measureContainer);
  }, [crop, measureContainer]);

  useEffect(() => {
    if (!crop?.rect) {
      setLayout((prev) =>
        prev.naturalWidth === 0 && prev.naturalHeight === 0
          ? prev
          : { ...prev, naturalWidth: 0, naturalHeight: 0 }
      );
    }
  }, [crop, resolvedSrc]);

  const resolvedVideoSrc = String(videoSrc || resolvedSrc || '').trim();
  const hasVideoSources = Array.isArray(videoSources) && videoSources.length > 0;
  const canRenderMedia = mode === 'video'
    ? Boolean(resolvedVideoSrc || hasVideoSources)
    : Boolean(String(resolvedSrc || '').trim());
  const effectiveForceSkeleton = Boolean(GLOBAL_FORCE_SKELETON);
  const hasExplicitPosition = /(^|\s)(static|fixed|absolute|relative|sticky)(\s|$)/.test(containerClassName);
  const wrapperClassName = hasExplicitPosition ? containerClassName.trim() : `relative ${containerClassName}`.trim();
  const fallbackRadiusClass =
    skeletonRadius === 'none'
      ? 'rounded-none'
      : skeletonRadius === 'sm'
      ? 'rounded-sm'
      : skeletonRadius === 'lg'
      ? 'rounded-lg'
      : skeletonRadius === 'xl'
      ? 'rounded-xl'
      : skeletonRadius === '2xl'
      ? 'rounded-2xl'
      : skeletonRadius === 'full'
      ? 'rounded-full'
      : 'rounded-md';

  const showSkeleton = isSkeletonEnabled && (effectiveForceSkeleton || !isLoaded);

  // Compute the crop rect style for images that have a crop.rect (manual rectangle crop).
  // This needs the container dimensions and the image's natural dimensions, both of which
  // are captured after mount and after the image loads.
  const rectStyle = useMemo(() => {
    if (!crop?.rect) return null;
    const { width, height, naturalWidth, naturalHeight } = layout;
    if (!width || !height || !naturalWidth || !naturalHeight) return null;
    const rx = crop.rect.x * naturalWidth;
    const ry = crop.rect.y * naturalHeight;
    const rw = crop.rect.width * naturalWidth;
    const rh = crop.rect.height * naturalHeight;
    if (!rw || !rh) return null;
    const scale = Math.max(width / rw, height / rh);
    const left = (width - rw * scale) / 2 - rx * scale;
    const top = (height - rh * scale) / 2 - ry * scale;
    return {
      width: `${naturalWidth * scale}px`,
      height: `${naturalHeight * scale}px`,
      left: `${left}px`,
      top: `${top}px`,
      maxWidth: 'none',
      ...style,
    };
  }, [crop, layout, style]);

  // For images with crop.zoom/x/y (no rect), use a simple CSS transform via the helper.
  const zoomTransformStyle = useMemo(() => {
    if (!crop || crop.rect) return null;
    return buildImageTransformStyle(image, style);
  }, [crop, image, style]);

  const hasRectCrop = Boolean(crop?.rect);
  const hasZoomCrop = Boolean(crop && !crop.rect);
  // Tailwind emits object-fit utilities in a fixed order, so a hardcoded default
  // class here would silently win over a caller's object-fit even if it appears
  // later in the class string. Only fall back to object-cover when the caller
  // hasn't picked their own fit (e.g. object-contain for lightboxes).
  const hasCustomObjectFit = /\bobject-(contain|cover|fill|none|scale-down)\b/.test(
    `${imgClassName} ${videoClassName}`
  );

  // Watermark config: support both boolean and object form (same as CroppedImage).
  const watermarkConfig =
    watermark && typeof watermark === 'object' ? watermark : watermark ? {} : null;

  // Right-click / drag protection handlers (only active when protectSaving is true).
  function handleContextMenu(event) {
    if (!protectSaving) return;
    event.preventDefault();
    event.stopPropagation();
  }
  function handleDragStart(event) {
    if (!protectSaving) return;
    event.preventDefault();
  }

  return (
    <div className={wrapperClassName} style={style}>
      {showSkeleton ? (
        <div
          aria-hidden="true"
          className={`absolute inset-0 z-1 ${effectiveForceSkeleton ? '' : ''}`.trim()}
        >
          {canRenderMedia ? (
            <ImageSkeleton
              src={mode === 'video' ? resolvedVideoSrc || '/logos/logo.svg' : resolvedSrc}
              alt=""
              className="h-full w-full"
              imgClassName={skeletonImgClassName}
              trackLoad={false}
              radius="none"
              style={skeletonStyle}
              aspectRatio={aspectRatio}
              width={width}
              height={height}
            />
          ) : (
            <div className={`relative h-full w-full overflow-hidden bg-[var(--dash-black)] ${fallbackRadiusClass}`.trim()}>
              <div className="absolute inset-0 bg-[var(--dash-black)]" />
              <div className="image-skeleton-shimmer absolute inset-y-0 left-[-45%] w-[45%]" />
              {/* eslint-disable-next-line @next/next/no-img-element */}
              <img
                src="/logos/logo.svg"
                alt=""
                className="image-skeleton-logo pointer-events-none absolute left-1/2 top-1/2 w-[72px] -translate-x-1/2 -translate-y-1/2 grayscale"
              />
              <style jsx>{`
                .image-skeleton-shimmer {
                  background: linear-gradient(
                    90deg,
                    transparent 0%,
                    rgba(255, 255, 255, 0.28) 50%,
                    transparent 100%
                  );
                  animation: image-skeleton-shimmer 1.5s ease-in-out infinite;
                }

                .image-skeleton-logo {
                  opacity: 0.34;
                  filter: grayscale(1) brightness(1.15);
                  animation: image-skeleton-logo 2s ease-in-out infinite;
                }

                @keyframes image-skeleton-shimmer {
                  0% {
                    transform: translateX(0);
                    opacity: 0;
                  }

                  20% {
                    opacity: 1;
                  }

                  100% {
                    transform: translateX(350%);
                    opacity: 0;
                  }
                }

                @keyframes image-skeleton-logo {
                  0%,
                  100% {
                    transform: translate(-50%, -50%) scale(1);
                    opacity: 0.22;
                  }

                  50% {
                    transform: translate(-50%, -50%) scale(1.04);
                    opacity: 0.5;
                  }
                }
              `}</style>
            </div>
          )}
          {effectiveForceSkeleton ? (
            <span className="pointer-events-none absolute bottom-2 left-2 rounded-md border border-[var(--dash-main)]/60 bg-black/75 px-2 py-1 text-[10px] font-semibold uppercase tracking-[0.14em] text-[var(--dash-main)]">
              Skeleton Preview
            </span>
          ) : null}
        </div>
      ) : null}

      {canRenderMedia ? (
        <div
          ref={cropRef}
          className={`relative h-full w-full overflow-hidden select-none ${showSkeleton ? 'opacity-0' : 'opacity-100 transition-opacity duration-300'}`.trim()}
          onContextMenu={handleContextMenu}
        >
          {mode === 'video' ? (
            <video
              src={shouldLoadVideo ? resolvedVideoSrc || undefined : undefined}
              poster={responsiveVideoPoster || videoPoster}
              className={`block h-full w-full ${hasCustomObjectFit ? '' : 'object-cover'} ${videoClassName || imgClassName}`.trim()}
              onLoadedData={handleVideoReady}
              onCanPlay={handleVideoReady}
              onError={handleError}
              autoPlay
              loop
              muted
              playsInline
              preload={shouldLoadVideo ? 'metadata' : 'none'}
              draggable={false}
              onContextMenu={handleContextMenu}
              onDragStart={handleDragStart}
              {...imageProps}
            >
              {shouldLoadVideo && hasVideoSources
                ? videoSources.map((source, index) => {
                    const srcValue = String(source?.src || '').trim();
                    if (!srcValue) return null;
                    const typeValue = String(source?.type || '').trim();
                    return <source key={`${srcValue}-${index}`} src={srcValue} type={typeValue || undefined} />;
                  })
                : null}
            </video>
          ) : (
            // eslint-disable-next-line @next/next/no-img-element
            <img
              src={resolvedSrc}
              srcSet={
                imageSources.length
                  ? imageSources.map((source) => `${source.src} ${source.width}w`).join(', ')
                  : undefined
              }
              sizes={imageSources.length ? '100vw' : undefined}
              alt={alt}
              loading={loading}
              fetchPriority={fetchpriority}
              decoding={decoding}
              className={`block h-full w-full ${hasCustomObjectFit ? '' : 'object-cover'} ${hasRectCrop ? 'absolute left-0 top-0 max-w-none' : ''} ${hasZoomCrop ? 'absolute left-1/2 top-1/2 max-w-none' : ''} ${imgClassName}`.trim()}
              style={hasRectCrop ? (rectStyle || style) : hasZoomCrop ? (zoomTransformStyle || style) : style}
              onLoad={handleLoad}
              onError={handleError}
              draggable={false}
              onContextMenu={handleContextMenu}
              onDragStart={handleDragStart}
              {...imageProps}
            />
          )}
          {watermarkConfig ? (
            <ImageWatermark
              logoSrc={watermarkConfig.logoSrc || '/logos/logo.svg'}
              opacity={typeof watermarkConfig.opacity === 'number' ? watermarkConfig.opacity : 0.15}
              backgroundSize={watermarkConfig.backgroundSize || '120px'}
              className={watermarkConfig.className || ''}
            />
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
